'use client'

/**
 * Shared step-by-step view primitives.
 *
 * The flogger detail pages (`flogger-media-tabs.tsx`) render numbered,
 * illustrated steps with a user-selectable list / grid / carousel view.
 * These primitives were factored out so that renderer — and any future
 * one — shares a single implementation; tweaks (a new view mode, a layout
 * fix, an a11y improvement) flow to every surface that imports them.
 * (The scene page formerly used them too, via the now-removed
 * `cas-media-tabs.tsx`; its redesigned player renders steps directly.)
 *
 * The data shape is the lowest common denominator:
 *   { _key, imageUrl, stepInstructions?, additionalComment? }
 *
 * `stepInstructions` / `additionalComment` are localePortableText maps
 * ({ en: [blocks], … }) rendered through PortableTextRenderer so the
 * full rich-text formatting (bold, links, lists) editors author shows
 * on the public page.
 *
 * Callers that have a different shape (e.g. floggers' nested
 * `image: { url }` and locale-keyed strings) are responsible for
 * mapping into this shape before passing in.
 *
 * Flogger PR-2a additions:
 *   - `StepItem.imageAlt` — the image asset's alt text, used by every view
 *     when set (otherwise the img alt falls back to the step text, as before).
 *     A click-to-enlarge button's accessible name is "View step N image full
 *     size" plus the ASSET alt only (never the step-text fallback, which the
 *     instructions right after the button already say);
 *   - `StepsCarousel` `current` / `onCurrentChange` — drive the carousel
 *     from outside (controlled), or just listen (uncontrolled);
 *   - `StepsCarousel` `keyboardNav` (opt-in) — ←/→ while focus is inside the
 *     carousel, or while it is hovered and the focused element does not use
 *     arrow keys itself (`hoverKeysAllowed`);
 *   - carousel a11y (always on, no visible change): `aria-roledescription=
 *     "carousel"` on a `region` named by the visible heading (`labelledBy`)
 *     or `label`, else a `group` (so unnamed carousels never become
 *     duplicate "Steps" landmarks); the counter + step text are one polite
 *     live region, so a move reads the new step; arrow buttons stay natively
 *     `disabled` at the ends (out of the tab order, as before), and when a
 *     KEYBOARD move disables or unmounts the focused control, focus goes to
 *     an arrow that can still move. All strings come from `articleView`.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { pickPortableText } from '@/lib/i18n/pickLocale'
import { portableTextToPlain } from '@/lib/i18n/portableTextTranslation'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import { PortableTextRenderer } from '@/components/content/portable-text-renderer'
import {
  carouselKeyDelta,
  clampStepIndex,
  fullSizeButtonLabel,
  hoverKeysAllowed,
} from '@/lib/content/step-view-primitives'

/**
 * A localePortableText value — keyed by locale, each value an array of
 * Sanity portable-text blocks. Tolerates a bare block array too.
 */
type LocalePortableText =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any[] | null | undefined> | any[] | null

export interface StepItem {
  _key: string
  imageUrl: string | null
  stepInstructions: LocalePortableText
  additionalComment: LocalePortableText
  /**
   * Alt text describing the photo (the image asset's `altText`). When set it
   * is the image's alt in every view; when absent the alt falls back to the
   * step text, then "Step N" (the behaviour before this field existed).
   */
  imageAlt?: string | null
}

/** Alt text for a step image: the asset's alt when given, else the step text. */
function stepImageAlt(step: StepItem, locale: string, index: number): string {
  const assetAlt = (step.imageAlt || '').trim()
  return assetAlt || stepAltText(step.stepInstructions, locale, index)
}

/** Plain-text fallback for an <img alt>, from a localePortableText value. */
function stepAltText(
  value: LocalePortableText,
  locale: string,
  index: number,
): string {
  const blocks = pickPortableText(value, locale)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plain = portableTextToPlain(blocks as any).trim()
  return plain || `Step ${index + 1}`
}

function StepCard({
  step,
  index,
  onImageClick,
}: {
  step: StepItem
  index: number
  /** When set, the image becomes a click-to-enlarge button (opens the caller's lightbox). */
  onImageClick?: (step: StepItem) => void
}) {
  const locale = useLocale()
  const t = useTranslations('articleView')
  const instructions = pickPortableText(step.stepInstructions, locale)
  const comment = pickPortableText(step.additionalComment, locale)
  const alt = stepImageAlt(step, locale, index)
  return (
    <>
      {step.imageUrl &&
        (onImageClick ? (
          <button
            type="button"
            onClick={() => onImageClick(step)}
            // The aria-label hides the img alt. It carries the ASSET alt only:
            // the step-text fallback would repeat the instructions below.
            aria-label={fullSizeButtonLabel(step.imageAlt, t('stepImageFullSize', { number: index + 1 }))}
            className="group block w-full cursor-zoom-in overflow-hidden"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={optimizeSanityImageUrl(step.imageUrl, 800)}
              alt={alt}
              className="aspect-3/4 w-full bg-gray-100 object-cover transition-transform duration-200 group-hover:scale-105"
            />
          </button>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={optimizeSanityImageUrl(step.imageUrl, 800)}
            alt={alt}
            className="aspect-3/4 w-full bg-gray-100 object-cover"
          />
        ))}
      <div className="p-3">
        {instructions.length > 0 && (
          <div className="prose prose-sm max-w-none text-gray-900 dark:text-gray-100">
            <PortableTextRenderer value={instructions as never} />
          </div>
        )}
        {comment.length > 0 && (
          <div className="prose prose-sm mt-1 max-w-none text-gray-600 dark:text-gray-400">
            <PortableTextRenderer value={comment as never} />
          </div>
        )}
      </div>
    </>
  )
}

type StepsProps = {
  steps: StepItem[]
  /** When set, step images become click-to-enlarge buttons calling this. */
  onImageClick?: (step: StepItem) => void
}

export function StepsList({ steps, onImageClick }: StepsProps) {
  return (
    <div className="space-y-6">
      {steps.map((step, i) => (
        <div key={step._key} className="flex gap-4 border p-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center bg-brand-700 text-sm font-bold text-white">
            {i + 1}
          </div>
          <div className="flex-1 space-y-2">
            <StepCard step={step} index={i} onImageClick={onImageClick} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function StepsGrid({ steps, onImageClick }: StepsProps) {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {steps.map((step, i) => (
        <div key={step._key} className="overflow-hidden border bg-white dark:bg-gray-900">
          <div className="relative">
            <StepCard step={step} index={i} onImageClick={onImageClick} />
            <span className="pointer-events-none absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center bg-brand-700 text-xs font-bold text-white">
              {i + 1}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

type StepsCarouselProps = StepsProps & {
  /**
   * Controlled index. When set, the carousel shows this step and asks for
   * changes through `onCurrentChange` instead of keeping its own state.
   * Omitted → uncontrolled (starts at the first step), as before.
   */
  current?: number
  /** Called with the next index whenever the reader moves (arrows, swipe,
   *  keys). Required to move a controlled carousel; optional otherwise. */
  onCurrentChange?: (index: number) => void
  /**
   * ←/→ step the carousel while focus is inside it, or while the pointer is
   * over it and the focused element (if any) does not use arrow keys itself
   * (`hoverKeysAllowed`: not a text field, a tablist/slider/menu-like widget,
   * media, or another carousel). Never a page-wide handler, so several
   * carousels on one page and form fields elsewhere are unaffected.
   * Off by default. Pass false while a lightbox (which has its own arrow
   * keys) is open.
   */
  keyboardNav?: boolean
  /**
   * id of the visible heading that names this carousel (preferred: the name
   * stays unique per block and keeps the heading's `lang`). Makes the root a
   * named `region` landmark.
   */
  labelledBy?: string
  /** Accessible name as a string, when there is no heading to point at. */
  label?: string
}

export function StepsCarousel({
  steps,
  onImageClick,
  current: currentProp,
  onCurrentChange,
  keyboardNav = false,
  labelledBy,
  label,
}: StepsCarouselProps) {
  const locale = useLocale()
  const t = useTranslations('articleView')
  const controlled = currentProp !== undefined
  const [internalCurrent, setInternalCurrent] = useState(0)
  const current = clampStepIndex(controlled ? currentProp : internalCurrent, steps.length)
  const rootRef = useRef<HTMLDivElement | null>(null)
  // A move can take focus away from the focused control: the arrow that
  // reaches an end becomes `disabled`, or the image button unmounts (next step
  // has no image). What happens next depends on how the move was made:
  //   - pointer (click, swipe): a clicked arrow that is about to be disabled
  //     lets go of focus first, as a disabled button did before this change,
  //     so Space/PageDown then scroll the page and no ring appears;
  //   - keyboard (keys, Enter/Space on an arrow): `pendingKeyboardMove` makes
  //     the effect below hand focus to an arrow that can still move, so
  //     keyboard navigation never dead-ends on <body> at the last step.
  // The root itself is never focusable (no tabIndex), so clicking the step
  // text does not give the whole carousel a focus ring.
  const pendingKeyboardMove = useRef<number | null>(null)
  const go = useCallback(
    (delta: number, via: 'keyboard' | 'pointer' = 'keyboard') => {
      const next = clampStepIndex(current + delta, steps.length)
      if (next === current) return
      const root = rootRef.current
      const active = document.activeElement
      const focusInside = !!root && !!active && root.contains(active)
      pendingKeyboardMove.current = focusInside && via === 'keyboard' ? delta : null
      if (focusInside && via === 'pointer' && active instanceof HTMLElement) {
        const disabling = delta > 0 ? next === steps.length - 1 : next === 0
        if (disabling && active.dataset.carouselNav === (delta > 0 ? 'next' : 'prev')) active.blur()
      }
      if (!controlled) setInternalCurrent(next)
      onCurrentChange?.(next)
    },
    [current, controlled, onCurrentChange, steps.length],
  )
  useEffect(() => {
    const delta = pendingKeyboardMove.current
    pendingKeyboardMove.current = null
    const root = rootRef.current
    if (delta === null || !root) return
    const active = document.activeElement
    // Only fix up focus the move took away (now on <body>, or still on an
    // arrow that just became disabled); never pull it from elsewhere.
    const disabledInside =
      !!active && root.contains(active) && (active as HTMLButtonElement).disabled === true
    if (active && active !== document.body && !disabledInside) return
    const arrow = (dir: 'prev' | 'next') =>
      root.querySelector<HTMLButtonElement>(`button[data-carousel-nav="${dir}"]:not(:disabled)`)
    const ahead: 'prev' | 'next' = delta > 0 ? 'next' : 'prev'
    ;(arrow(ahead) ?? arrow(ahead === 'next' ? 'prev' : 'next'))?.focus({ preventScroll: true })
  }, [current])

  // Scoped keyboard navigation (opt-in). Focus inside the carousel: the
  // root's onKeyDown handles the key (no window listener). Hover: a window
  // listener exists only while the pointer is over THIS carousel, and acts
  // unless the focused element owns the arrow keys (a field, a tablist /
  // slider-like widget, media, or another carousel; see hoverKeysAllowed).
  const [hovered, setHovered] = useState(false)
  const onRootKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const delta = carouselKeyDelta(e.nativeEvent)
    if (delta === 0) return
    e.preventDefault()
    go(delta)
  }
  useEffect(() => {
    if (!keyboardNav || !hovered || steps.length < 2) return
    const onKey = (e: KeyboardEvent) => {
      const root = rootRef.current
      // Keys from inside the carousel are handled by onRootKeyDown.
      if (root && e.target instanceof Node && root.contains(e.target)) return
      if (!hoverKeysAllowed(document.activeElement)) return
      const delta = carouselKeyDelta(e)
      if (delta === 0) return
      e.preventDefault()
      go(delta)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [keyboardNav, hovered, steps.length, go])

  // Swipe navigation (mobile). Horizontal only, past a threshold — so a tap
  // still enlarges (when onImageClick is set) and vertical scrolls pass through.
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = (e: ReactTouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const onTouchEnd = (e: ReactTouchEvent) => {
    const start = touchStart.current
    touchStart.current = null
    if (!start) return
    const dx = e.changedTouches[0].clientX - start.x
    const dy = e.changedTouches[0].clientY - start.y
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1, 'pointer')
  }
  const step = steps[current]
  if (!step) return null

  const instructions = pickPortableText(step.stepInstructions, locale)
  const comment = pickPortableText(step.additionalComment, locale)
  const alt = stepImageAlt(step, locale, current)
  const stringLabel = label?.trim()
  // Named by a heading or a label: a `region` landmark. Unnamed: a `group`
  // (APG allows either), so several unnamed carousels on one page are not
  // duplicate "Steps" landmarks.
  const named = !!labelledBy || !!stringLabel

  return (
    <div
      ref={rootRef}
      role={named ? 'region' : 'group'}
      // Stable hook for hoverKeysAllowed (the roledescription is localized).
      data-steps-carousel=""
      aria-roledescription={t('carouselRoleDescription')}
      aria-labelledby={labelledBy || undefined}
      aria-label={labelledBy ? undefined : stringLabel || t('stepsLabel')}
      onKeyDown={keyboardNav && steps.length > 1 ? onRootKeyDown : undefined}
      // Hover is tracked even while keyboardNav is off (e.g. a lightbox is
      // open) so it is never stale when the keys switch back on.
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="relative select-none overflow-hidden bg-white dark:bg-gray-950"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {step.imageUrl ? (
          onImageClick ? (
            <button
              type="button"
              onClick={() => onImageClick(step)}
              // Asset alt only (not the step-text fallback the live text says).
              aria-label={fullSizeButtonLabel(step.imageAlt, t('stepImageFullSize', { number: current + 1 }))}
              className="block w-full cursor-zoom-in"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeSanityImageUrl(step.imageUrl, 1600)}
                alt={alt}
                className="mx-auto block max-h-[600px] w-full object-contain"
                draggable={false}
              />
            </button>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={optimizeSanityImageUrl(step.imageUrl, 1600)}
              alt={alt}
              className="mx-auto block max-h-[600px] w-full object-contain"
              draggable={false}
            />
          )
        ) : (
          <div className="flex aspect-video w-full items-center justify-center text-4xl font-bold text-gray-300">
            {current + 1}
          </div>
        )}
        {steps.length > 1 && (
          <CarouselOverlay
            current={current}
            total={steps.length}
            go={go}
            // With click-to-enlarge, the image owns the click — so navigate via
            // edge arrow BUTTONS instead of full-half click zones (which would
            // swallow the enlarge tap).
            clickable={!!onImageClick}
          />
        )}
      </div>
      {/* Polite live region over the counter AND the step text (the APG
          carousel puts aria-live on the slide container): a move (arrow
          buttons, keys, swipe) reads the new step, not only its number. The
          visible counter keeps its text (no visual change) and is hidden
          from AT; the localized copy beside it is what is announced. */}
      <div className="mt-4" aria-live="polite" aria-atomic="true">
        <p className="text-xs font-medium text-brand-700" aria-hidden="true">
          Step {current + 1} of {steps.length}
        </p>
        <span className="sr-only">{t('stepCounter', { current: current + 1, total: steps.length })}</span>
        {instructions.length > 0 && (
          <div className="prose prose-sm mt-1 max-w-none text-gray-900 dark:text-gray-100">
            <PortableTextRenderer value={instructions as never} />
          </div>
        )}
        {comment.length > 0 && (
          <div className="prose prose-sm mt-1 max-w-none text-gray-600 dark:text-gray-400">
            <PortableTextRenderer value={comment as never} />
          </div>
        )}
      </div>
    </div>
  )
}

export function CarouselOverlay({
  current,
  total,
  go,
  clickable = false,
}: {
  current: number
  total: number
  /** `via` says how the move was made, for StepsCarousel's focus fix-up. */
  go: (delta: number, via?: 'keyboard' | 'pointer') => void
  /** Edge arrow BUTTONS (image handles its own click) instead of full-half click zones. */
  clickable?: boolean
}) {
  const t = useTranslations('articleView')
  // Natively `disabled` at either end (out of the tab order, hidden, as
  // before). StepsCarousel moves focus off an arrow a keyboard move disables.
  // A click's `detail` is 0 when Enter/Space activated the button.
  const atStart = current === 0
  const atEnd = current === total - 1
  const via = (e: ReactMouseEvent) => (e.detail === 0 ? 'keyboard' : 'pointer')
  if (clickable) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => go(-1, via(e))}
          disabled={atStart}
          data-carousel-nav="prev"
          aria-label={t('previousStep')}
          className="absolute inset-y-0 left-0 z-20 flex w-12 items-center justify-center transition-opacity disabled:pointer-events-none disabled:opacity-0"
        >
          <span className="bg-black/40 p-2">
            <ChevronLeft className="h-6 w-6 text-white" />
          </span>
        </button>
        <button
          type="button"
          onClick={(e) => go(1, via(e))}
          disabled={atEnd}
          data-carousel-nav="next"
          aria-label={t('nextStep')}
          className="absolute inset-y-0 right-0 z-20 flex w-12 items-center justify-center transition-opacity disabled:pointer-events-none disabled:opacity-0"
        >
          <span className="bg-black/40 p-2">
            <ChevronRight className="h-6 w-6 text-white" />
          </span>
        </button>
      </>
    )
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => go(-1, via(e))}
        disabled={atStart}
        data-carousel-nav="prev"
        aria-label={t('previousStep')}
        className="absolute inset-y-0 left-0 w-1/2 cursor-pointer disabled:cursor-default"
      />
      <button
        type="button"
        onClick={(e) => go(1, via(e))}
        disabled={atEnd}
        data-carousel-nav="next"
        aria-label={t('nextStep')}
        className="absolute inset-y-0 right-0 w-1/2 cursor-pointer disabled:cursor-default"
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 flex w-12 items-center justify-center transition-opacity ${
          atStart ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="bg-black/40 p-2">
          <ChevronLeft className="h-6 w-6 text-white" />
        </div>
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-center transition-opacity ${
          atEnd ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="bg-black/40 p-2">
          <ChevronRight className="h-6 w-6 text-white" />
        </div>
      </div>
    </>
  )
}
